/** カード共通: タイトル行・生成時の設定・本文・画像 */

import type { ReactNode } from 'react'
import type { QueueItem } from '../../lib/postTypes'
import { useFormats, useImageStyles } from '../../lib/queries'
import { kindLabel } from '../../lib/queueRules'

export function PostBody({ item, badge }: { item: QueueItem; badge?: ReactNode }) {
  const { post, activeVersion, credits } = item
  // 型・画像スタイルのラベル辞書（バッジ表示用）
  const formats = useFormats()
  const imageStyles = useImageStyles()
  const formatLabel = (key?: string) => formats.data?.find(f => f.key === key)?.label ?? key
  const styleLabel = (key?: string) => imageStyles.data?.find(s => s.key === key)?.label ?? key

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="flex min-w-0 items-center gap-2 font-bold">
          {badge}
          <span className="truncate">{post.title || '(無題)'}</span>
        </p>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 text-xs text-ink-muted">
          {activeVersion?.score != null && (
            <span className="gm-chip gm-chip-neutral gm-num">批評 {activeVersion.score}点</span>
          )}
          {activeVersion && (
            <span className="gm-chip gm-chip-neutral gm-num">
              {(activeVersion.variants.x?.text ?? '').length}字
            </span>
          )}
          {/* 消費実績は丸めない（残高の 5 単位繰上げはしない） */}
          {!!credits && (
            <span className="text-ink-muted" title="この投稿の生成に使ったクレジット">
              <span className="gm-num">{credits}</span>cr
            </span>
          )}
          <span>{kindLabel(post.kind)}</span>
        </div>
      </div>
      {/* 生成時の設定（版のブリーフから復元） */}
      {activeVersion?.brief && (
        <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ink-muted" data-testid="brief-meta">
          {activeVersion.brief.format && <span>型: {formatLabel(activeVersion.brief.format)}</span>}
          {(activeVersion.variants.x?.image || activeVersion.brief.image) && (
            <span>画像: {styleLabel(activeVersion.variants.x?.image?.style ?? activeVersion.brief.image?.style)}</span>
          )}
          {activeVersion.brief.image?.koma && (
            <span>
              {activeVersion.brief.image.koma.format === 'grid'
                ? '4コマ(2×2)'
                : `縦${activeVersion.brief.image.koma.panels}コマ`}
            </span>
          )}
          {!!activeVersion.brief.targetLength && <span>目安<span className="gm-num">{activeVersion.brief.targetLength}</span>字</span>}
          {!!activeVersion.brief.customPrompt && <span>追加指示あり</span>}
        </p>
      )}
      {/* 投稿本文（利用者の作品）。文字はそのまま、囲む枠だけ揃える */}
      {activeVersion && (
        <p className="mt-2 whitespace-pre-wrap rounded-ctl bg-surface-2 p-3 text-sm">
          {activeVersion.variants.x?.text ?? '(本文なし)'}
        </p>
      )}
      {activeVersion?.variants.x?.image && (
        <a href={activeVersion.variants.x.image.url} target="_blank" rel="noreferrer" className="mt-2 block w-fit">
          <img src={activeVersion.variants.x.image.url} alt="投稿画像"
            className="max-h-40 rounded-ctl border border-line object-cover" />
        </a>
      )}
    </>
  )
}
